import { Dispatch, FC, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Button, Checkbox, Divider, Empty, Space, Spin, Typography } from 'antd';
import TaskModal from 'components/TaskModal';
import { convertMinsToHrsMins, getQueryValue, localRandomUuid, renderTime } from 'utils/string.utils';
import { mapModelToUploadFileApi } from 'utils/array.utils';
import useWebSocket from 'utils/useWebSocket';
import { TIME_ENTRY_WS_EVENT, isLocalEcho, TimeEntryWsEvent } from 'services/timeTracking';
import RequestTaskCard from '../RequestTaskCard';
import RelateTaskRequestModal from '../RelateTaskRequestModal';

const { Title } = Typography;

const getAllRelatedTasksUuid = (tasks: any[]): string[] => {
  return tasks.reduce((acc, task) => [...acc, task.uuid, ...getAllRelatedTasksUuid(task.subtasks)], []);
};

interface RequestTaskTabProps {
  request: Record<string, any>;
  tasks: any[];
  tasksLoading: boolean;
  fetchRequestTasks: () => Promise<void>;
  pageUrl: string;
  showTimeReport?: boolean;
  setRequest: Dispatch<any>;
}

const RequestTaskTab: FC<RequestTaskTabProps> = ({
  request,
  tasks = [],
  tasksLoading,
  fetchRequestTasks,
  pageUrl,
  showTimeReport,
  setRequest
}) => {
  const [openRelateTaskModal, setOpenRelateTaskModal] = useState(false);
  const [showAllTasks, setShowAllTasks] = useState(false);

  const history = useHistory();
  const taskUuid = getQueryValue(history.location.search, 'uuid');
  const files = useMemo(() => mapModelToUploadFileApi(request.uploads), [request.uploads]);
  const totalTime = request.tasks_total_time;

  const filteredTasks = useMemo(() => {
    const filterTaskTree = (tasksList: any[], showAll: boolean): any[] => {
      if (showAll) return tasksList;
      return tasksList
        .filter((task) => task.task_status !== 'completed' && task.task_status !== 'cancelled')
        .map((task) => ({
          ...task,
          subtasks: task.subtasks ? filterTaskTree(task.subtasks, showAll) : [],
        }));
    };
    return filterTaskTree(tasks, showAllTasks);
  }, [tasks, showAllTasks]);

  const removeTask = (removeTaskUuid: string) => {
    fetchRequestTasks();
    setRequest({
      ...request,
      tasks: request.tasks.filter((task: any) => task.uuid !== removeTaskUuid),
    });
  };

  useWebSocket({
    channelName: 'model.changes',
    listen: [
      {
        event: '.app.models.projects.task',
        callback: (event: any) => {
          if (event.payload?.ref_token === localRandomUuid) return;

          fetchRequestTasks();
        },
      },
      {
        event: TIME_ENTRY_WS_EVENT,
        callback: (event: TimeEntryWsEvent) => {
          if (isLocalEcho(event)) return;

          const matchesTask =
            event.payload.source === 'task' && getAllRelatedTasksUuid(request.tasks).includes(event.payload.source_uuid);

          if (
            (showTimeReport && matchesTask) ||
            (!showTimeReport && request.project.uuid === event.payload.project_uuid)
          ) {
            fetchRequestTasks();
          }
        },
      },
    ],
  });

  return (
    <div style={{ height: '100%' }}>
      {openRelateTaskModal && (
        <RelateTaskRequestModal
          uuid={request.uuid}
          projectUuid={request.project?.uuid}
          onClose={() => setOpenRelateTaskModal(false)}
          onSuccess={() => fetchRequestTasks()}
        />
      )}

      <TaskModal
        visible={taskUuid}
        project={request}
        initialFiles={taskUuid === 'create' ? files : undefined}
        onClose={() => history.push(`${pageUrl}/${request.uuid}`)}
        onSaved={() => {
          fetchRequestTasks();
        }}
      />

      <div className="primary" style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
        <span>Total time</span>
        <span>
          <b>{renderTime(convertMinsToHrsMins(totalTime))}</b>
        </span>
      </div>

      <Divider style={{ margin: '10px -10px', width: 'calc(100% + 20px)' }} className="primary" />

      <div
        style={{
          display: 'inline-flex',
          justifyContent: 'space-between',
          width: '100%',
          alignItems: 'center',
        }}
      >
        <Title level={4} className="primary no-margin ">
          Tasks
        </Title>

        <Space style={{ paddingBottom: 8 }}>
          <Checkbox
            checked={showAllTasks}
            disabled={tasks.length === 0}
            onChange={(event) => setShowAllTasks(event.target.checked)}
          >
            Show all
          </Checkbox>

          <Link to={`${pageUrl}/${request.uuid}/tasks?uuid=create`}>
            <Button type="primary" size="large">
              Create task
            </Button>
          </Link>

          <Button type="default" size="large" onClick={() => setOpenRelateTaskModal(true)}>
            Relate task
          </Button>
        </Space>
      </div>

      <div style={{ overflowY: 'auto', marginRight: -4, padding: '6px 4px 8px 0', height: 'calc(100% - 90px)' }}>
        <Spin spinning={tasksLoading}>
          {filteredTasks.map((task) => {
            return (
              <RequestTaskCard
                key={task.uuid}
                requestUuid={request.uuid}
                task={task}
                listTasks={tasks}
                onRemoved={() => removeTask(task.uuid)}
              />
            );
          })}

          {!filteredTasks.length && <Empty description="No tasks" style={{ marginTop: 42 }} />}
        </Spin>
      </div>
    </div>
  );
};

export default RequestTaskTab;
